import { openLightbox, type LightboxImage } from './lightbox';

export interface CarouselImage extends LightboxImage {
  path?: string;
  url: string;
  thumbUrl?: string;
  order?: number;
}

/**
 * Render a hardware-accelerated, touch-swipeable infinite-loop carousel into container element.
 * Compatible with room cards, room types, restaurant hero banner, and spa hero banner.
 */
export function renderCarousel(container: HTMLElement, images: CarouselImage[]): void {
  if (!container || !images || images.length === 0) return;

  // Single image case
  if (images.length === 1) {
    const img = images[0];
    container.innerHTML = `
      <div class="carousel">
        <div class="carousel-track">
          <div class="carousel-item" data-real-index="0" style="cursor:pointer;">
            <img src="${img.thumbUrl || img.url}" alt="image" loading="lazy" />
          </div>
        </div>
      </div>
    `;
    const itemEl = container.querySelector('.carousel-item') as HTMLElement;
    itemEl?.addEventListener('click', () => openLightbox(images, 0));
    return;
  }

  // Multiple images: Create Infinite Loop track with Clones
  // Item sequence: [ Clone of N-1, Img 0, Img 1, ..., Img N-1, Clone of 0 ]
  const totalCount = images.length;
  const cloneLast = images[totalCount - 1];
  const cloneFirst = images[0];

  const trackItems = [
    { ...cloneLast, isClone: true, realIndex: totalCount - 1 },
    ...images.map((img, i) => ({ ...img, isClone: false, realIndex: i })),
    { ...cloneFirst, isClone: true, realIndex: 0 },
  ];

  container.innerHTML = `
    <div class="carousel">
      <div class="carousel-track">
        ${trackItems
          .map(
            (img) => `
          <div class="carousel-item" data-real-index="${img.realIndex}" style="cursor:pointer;">
            <img data-src="${img.thumbUrl || img.url}" alt="image" loading="lazy" />
          </div>
        `
          )
          .join('')}
      </div>
      <button class="carousel-prev" aria-label="Previous">‹</button>
      <button class="carousel-next" aria-label="Next">›</button>
      <div class="carousel-indicators">
        ${images.map((_, i) => `<button class="indicator${i === 0 ? ' active' : ''}" data-index="${i}"></button>`).join('')}
      </div>
    </div>
  `;

  const track = container.querySelector('.carousel-track') as HTMLElement;
  const prev = container.querySelector('.carousel-prev') as HTMLElement;
  const next = container.querySelector('.carousel-next') as HTMLElement;
  const indicators = Array.from(container.querySelectorAll('.indicator')) as HTMLElement[];
  if (!track) return;

  // Lazy-load images
  track.querySelectorAll('img[data-src]').forEach((imgEl: Element) => {
    const img = imgEl as HTMLImageElement;
    img.src = img.dataset.src || '';
  });

  const items = Array.from(track.querySelectorAll('.carousel-item')) as HTMLElement[];

  // Position at real first image (index 1 in track)
  requestAnimationFrame(() => {
    const firstReal = items[1];
    if (firstReal) {
      track.scrollLeft = firstReal.offsetLeft;
    }
  });

  let isTeleporting = false;
  let isTouching = false;
  let fallbackTimer: ReturnType<typeof setTimeout> | null = null;

  // Silent instant teleport: DOES NOT touch scrollSnapType (Zero Composite Invalidation)
  // Jumps to the target snap point with behavior: 'instant' using exact DOM offsetLeft
  const silentTeleport = (targetIndex: number) => {
    const targetEl = items[targetIndex];
    if (!targetEl) return;
    isTeleporting = true;
    track.scrollTo({ left: targetEl.offsetLeft, behavior: 'instant' as ScrollBehavior });
    setTimeout(() => {
      isTeleporting = false;
    }, 50);
  };

  // Check if we have rested on a clone and need to teleport
  const onScrollRest = () => {
    if (isTeleporting || isTouching) return;
    const scrollLeft = track.scrollLeft;

    const cloneFirstEl = items[totalCount + 1]; // Clone of Image 0 at right boundary
    const cloneLastEl = items[0];               // Clone of Image N-1 at left boundary
    if (!cloneFirstEl || !cloneLastEl) return;

    // Settled on Clone of First Image (right end):
    // Instantly jump to Real Image 0 (index 1)
    if (Math.abs(scrollLeft - cloneFirstEl.offsetLeft) < 15) {
      silentTeleport(1);
      indicators.forEach((btn, i) => btn.classList.toggle('active', i === 0));
    }
    // Settled on Clone of Last Image (left end):
    // Instantly jump to Real Image N-1 (index totalCount)
    else if (Math.abs(scrollLeft - cloneLastEl.offsetLeft) < 15) {
      silentTeleport(totalCount);
      indicators.forEach((btn, i) => btn.classList.toggle('active', i === totalCount - 1));
    }
  };

  // Update active dot indicator based on current view
  const updateIndicators = () => {
    const scrollLeft = track.scrollLeft;
    let closestIndex = 1;
    let minDiff = Infinity;
    items.forEach((item, idx) => {
      const diff = Math.abs(scrollLeft - item.offsetLeft);
      if (diff < minDiff) {
        minDiff = diff;
        closestIndex = idx;
      }
    });

    let realIndex = 0;
    if (closestIndex <= 0) {
      realIndex = totalCount - 1;
    } else if (closestIndex >= totalCount + 1) {
      realIndex = 0;
    } else {
      realIndex = closestIndex - 1;
    }
    indicators.forEach((btn, i) => btn.classList.toggle('active', i === realIndex));
  };

  // 1. Modern Web Standard: scrollend event
  track.addEventListener('scrollend', () => {
    if (fallbackTimer) clearTimeout(fallbackTimer);
    onScrollRest();
  });

  // 2. Real-time indicator updates during scroll + Fallback timer
  track.addEventListener(
    'scroll',
    () => {
      if (isTeleporting) return;
      updateIndicators();

      if (fallbackTimer) clearTimeout(fallbackTimer);
      fallbackTimer = setTimeout(() => {
        onScrollRest();
      }, 70);
    },
    { passive: true }
  );

  // Guard against teleporting while user's finger is on the screen
  track.addEventListener(
    'touchstart',
    () => {
      isTouching = true;
    },
    { passive: true }
  );

  const onTouchEnd = () => {
    isTouching = false;
    if (fallbackTimer) clearTimeout(fallbackTimer);
    fallbackTimer = setTimeout(() => {
      onScrollRest();
    }, 80);
  };

  track.addEventListener('touchend', onTouchEnd, { passive: true });
  track.addEventListener('touchcancel', onTouchEnd, { passive: true });

  // Next & Prev Arrow Clicks
  next?.addEventListener('click', (e) => {
    e.stopPropagation();
    if (isTeleporting) return;
    const currentScroll = track.scrollLeft;
    const currentReal = items.findIndex(
      (item, idx) => idx >= 1 && idx <= totalCount && Math.abs(currentScroll - item.offsetLeft) < 20
    );

    if (currentReal >= totalCount) {
      // At last image: smooth scroll forward to Clone 0
      const cloneFirstEl = items[totalCount + 1];
      if (cloneFirstEl) {
        track.scrollTo({ left: cloneFirstEl.offsetLeft, behavior: 'smooth' });
      }
    } else {
      const nextEl = items[(currentReal > 0 ? currentReal : 1) + 1];
      if (nextEl) {
        track.scrollTo({ left: nextEl.offsetLeft, behavior: 'smooth' });
      }
    }
  });

  prev?.addEventListener('click', (e) => {
    e.stopPropagation();
    if (isTeleporting) return;
    const currentScroll = track.scrollLeft;
    const currentReal = items.findIndex(
      (item, idx) => idx >= 1 && idx <= totalCount && Math.abs(currentScroll - item.offsetLeft) < 20
    );

    if (currentReal <= 1) {
      // At first image: smooth scroll backward to Clone Last
      const cloneLastEl = items[0];
      if (cloneLastEl) {
        track.scrollTo({ left: cloneLastEl.offsetLeft, behavior: 'smooth' });
      }
    } else {
      const prevEl = items[(currentReal > 0 ? currentReal : 1) - 1];
      if (prevEl) {
        track.scrollTo({ left: prevEl.offsetLeft, behavior: 'smooth' });
      }
    }
  });

  // Open Lightbox on image click
  items.forEach((itemEl) => {
    itemEl.addEventListener('click', () => {
      const idx = parseInt(itemEl.getAttribute('data-real-index') || '0', 10);
      openLightbox(images, idx);
    });
  });

  // Indicator clicks
  indicators.forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (isTeleporting) return;
      const realIdx = parseInt(btn.getAttribute('data-index') || '0', 10);
      const targetEl = items[realIdx + 1];
      if (targetEl) {
        track.scrollTo({ left: targetEl.offsetLeft, behavior: 'smooth' });
      }
    });
  });
}
